import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Banner, Box, Button, Collapsible, Icon, Spinner, Text } from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import { Circle, CircleCheck, CircleDashed } from "lucide-react";
import clsx from "clsx";
import { format } from "date-fns";
import { enUS, lt } from "date-fns/locale";
import DataTable from "components/datatable/Datatable";
import useIsPhone from "features/files/use-is-phone";
import { buildPreviewColumn } from "./invoice-doc-preview";
import { touchHitAreaClass } from "./invoice-doc-ui";
import { buildSelectColumn } from "./invoice-inbox-bulk-bar";
import { TAB_LABEL_KEYS } from "./invoice-inbox-tabs";
import PaginationControls from "components/pagination/PaginationControls";
import { Column, FooterRow } from "types/Datatable";
import {
  InvoiceExpectationPeriod,
  PurchaseDocFilters,
  PurchaseDocListItem,
  PurchaseDocStatus,
  PurchaseDocTab,
  PurchaseListPagination,
  PurchasePeriod,
} from "types/InvoiceInbox";
import {
  PeriodExportError,
  downloadPurchasePeriodExport,
  downloadPurchasePeriodRegisterPdf,
  fetchInvoiceExpectations,
  fetchPurchaseDocs,
  fetchPurchasePeriods,
  setPurchaseDocBookkept,
  setPurchaseDocMarkedPaid,
} from "services/invoice-inbox.service";
import {
  formatEurEquivalent,
  getStatusLabelKey,
  invoiceNumberWithSeries,
  getStatusTone,
  isBookkeepable,
  isForeignCurrency,
  isMarkedPaid,
  isMarkedPaidAutomatically,
  isOverdue,
  toAmount,
} from "utils/invoiceInboxStatus";
import { formatPrettyDate } from "utils/helperFunctions";
import { isUncertainPvmLine, pvmCodeLabel, pvmReasonLabel } from "utils/pvmClassifier";

/**
 * The month-handover screen: year -> month, each month expandable into the
 * summary an accountant types from.
 *
 * Headers are NOT company-wide any more (see erp-backend
 * periods/fetch.js/register-query.js): every number in a month header — the
 * doc count, the "entered" badge, the totals — comes from the SAME tab +
 * filters query as the rows a click would expand, so a header never promises
 * what the tab cannot show. A month with no rows under the current tab is a
 * GREY row: it stays in the tree (the month itself is not empty, just not
 * this tab's) with only a per-status breakdown, cannot be expanded, and shows
 * no totals or buttons.
 */

// A route param cannot be null — this is the sentinel the export endpoint and
// the docs/fetch `period` filter both agree on for "no accounting period".
const UNASSIGNED_KEY = "unassigned";

const periodKey = (period: string | null): string => period ?? UNASSIGNED_KEY;
const keyToPeriod = (key: string): string | null => (key === UNASSIGNED_KEY ? null : key);

// The month's per-status breakdown (over the WHOLE month, `monthTotal`'s
// universe — see PurchasePeriod), turned into the pills under the header.
// Order and mapping are fixed: approved+exported are shown as one "approved"
// bucket (the accountant does not care which of the two it landed as), split
// further into "approved" (still to enter) and "bookkept" (already entered —
// its own tab, see erp-backend's `bookkept` tab split), and "new" has no tab
// of its own to link to.
type PillBucket = { key: PurchaseDocStatus | "bookkept"; count: number; tab: PurchaseDocTab | null };

function buildPillBuckets(p: PurchasePeriod): PillBucket[] {
  const { byStatus } = p;
  const approvedBookkept = p.approvedBookkept || 0;
  const approvedTotal = byStatus.approved + byStatus.exported;
  return (
    [
      { key: "needs_review", count: byStatus.needs_review, tab: "needs_review" },
      { key: "awaiting_approval", count: byStatus.awaiting_approval, tab: "awaiting_approval" },
      { key: "approved", count: approvedTotal - approvedBookkept, tab: "approved" },
      { key: "bookkept", count: approvedBookkept, tab: "bookkept" },
      { key: "rejected", count: byStatus.rejected, tab: "rejected" },
      { key: "new", count: byStatus.new, tab: null },
    ] as PillBucket[]
  ).filter((bucket) => bucket.count > 0);
}

export const PILL_TONE_CLASS: Record<PurchaseDocStatus | "bookkept", string> = {
  new: "bg-gray-100 text-gray-600",
  needs_review: "bg-amber-50 text-amber-800",
  awaiting_approval: "bg-amber-50 text-amber-800",
  approved: "bg-green-50 text-green-800",
  bookkept: "bg-green-50 text-green-800",
  rejected: "bg-gray-100 text-gray-600",
  exported: "bg-green-50 text-green-800",
};

const PILL_CLASS = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs";

type MonthState = {
  loading: boolean;
  error: boolean;
  docs: PurchaseDocListItem[];
  pagination: PurchaseListPagination | null;
  page: number;
};

const EMPTY_MONTH_STATE: MonthState = { loading: false, error: false, docs: [], pagination: null, page: 1 };

// The background refresh: fast while something is still being read (its
// supplier, amounts and month only appear once it is), slow otherwise. Same
// two speeds the flat list polled at before the tree became the only view.
const READING_POLL_MS = 4000;
const IDLE_POLL_MS = 60000;

type Props = {
  companyId: string;
  tab: PurchaseDocTab;
  filters: PurchaseDocFilters;
  /** Bumped by the parent whenever the document panel changes something. */
  reloadToken: number;
  rowUrl: (item: PurchaseDocListItem) => string;
  /** Opens the file itself — the same peek the flat register offers. */
  onPreview: (item: PurchaseDocListItem) => void;
  /**
   * Bulk selection. Owned by the register so the tree and the flat list share
   * one bulk bar; the tree only ticks rows and reports which rows it shows.
   */
  selectedIds: Set<string>;
  selectionDisabled: boolean;
  /** Rows the last bulk action refused, highlighted like in the flat list. */
  failedIds: Set<string>;
  onToggleSelected: (id: string) => void;
  onSetManySelected: (ids: string[], selected: boolean) => void;
  /** Every open month's current page — what a selection may refer to. */
  onShownDocsChange: (docs: PurchaseDocListItem[]) => void;
  /** A status pill's link target: same tab-switch, tree view, this month. */
  chipUrl: (tab: PurchaseDocTab, periodKey: string) => string;
  /** `?period=` from the URL — a chip landing here auto-expands that month. */
  expandPeriodKey: string | null;
  /**
   * A bookkept toggle on the 'approved' or 'bookkept' tab moves the document
   * to the OTHER tab (see erp-backend's split) — unlike every other tab,
   * where toggling it leaves the row in place. Bumps the register's own
   * reload token so the headers, the tab counts and (once switched to) the
   * other tab's view all catch up, the same way any other write here does.
   */
  onDocMoved: () => void;
  /**
   * The register's tab badges went stale without the tree's own rows needing
   * a reload — an Įvesta toggle on a bookkeepable document moves it between
   * "Patvirtintos" and "Įvestos" whichever tab it was ticked on, and the
   * background poll below may have brought new mail. The register refetches
   * its counts; nothing here reloads.
   */
  onCountsStale?: () => void;
  /** A write of the register's own is running (upload, mail check, bulk
   * action) — the background poll waits it out, as the flat list's did. */
  pollPaused?: boolean;
  /**
   * Shown instead of this component's own placeholder when there are no
   * periods at all under the tab+filters being browsed — the register owns
   * the actual content (heading, hint, a settings link) because it also knows
   * whether any filter is active and whether the viewer can manage settings.
   */
  emptyState: React.ReactNode;
};

function monthLabel(period: string, language: string): string {
  const [yearStr, monthStr] = period.split("-");
  const date = new Date(Number(yearStr), Number(monthStr) - 1, 1);
  const label = format(date, "LLLL", { locale: language === "lt" ? lt : enUS });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function exportErrorMessage(err: any, t: (key: string, options?: any) => string): string {
  const code = (err as PeriodExportError)?.code;
  if (code === "TOO_MANY_DOCUMENTS") {
    return t("invoice_inbox_export_too_many", {
      count: err.count,
      limit: err.limit,
      defaultValue:
        "This month has {{count}} documents — more than one download can package at once (limit {{limit}}). Narrow the date range, or download it in smaller parts.",
    });
  }
  if (code === "EXPORT_TOO_LARGE") {
    const limitMb = Math.round((err.limit || 0) / (1024 * 1024));
    return t("invoice_inbox_export_too_large", {
      limit: limitMb,
      defaultValue:
        "This month's files total more than one download can package at once (limit {{limit}} MB). Narrow the date range, or download it in smaller parts.",
    });
  }
  if (code === "INVALID_PERIOD") {
    return t("invoice_inbox_export_invalid_period", { defaultValue: "This period could not be exported." });
  }
  return t("invoice_inbox_export_failed", { defaultValue: "Could not prepare the download. Try again." });
}

export default function InvoiceInboxPeriodTree({
  companyId,
  tab,
  filters,
  reloadToken,
  rowUrl,
  onPreview,
  selectedIds,
  selectionDisabled,
  failedIds,
  onToggleSelected,
  onSetManySelected,
  onShownDocsChange,
  chipUrl,
  expandPeriodKey,
  onDocMoved,
  emptyState,
  onCountsStale,
  pollPaused = false,
}: Props) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const [periods, setPeriods] = useState<PurchasePeriod[]>([]);
  // "Nothing has ever been applied yet", which is what the spinner means.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [monthState, setMonthState] = useState<Record<string, MonthState>>({});

  const [busyDocId, setBusyDocId] = useState<string | null>(null);
  // Bumped at the start and end of every write made from this tree (the
  // Įvesta and Apmokėta toggles). A background refresh remembers the value it
  // started under and drops its response if it moved: one fetched before the
  // click would otherwise land after it and paint the old value back over it.
  const writeSeqRef = useRef(0);
  const [bookkeptError, setBookkeptError] = useState<string | null>(null);
  const [paidBusyDocId, setPaidBusyDocId] = useState<string | null>(null);
  const [paidError, setPaidError] = useState<string | null>(null);

  // Monthly-invoice expectations, keyed by 'YYYY-MM' — shown inline on every
  // month row (see renderMonthRow), not just as the overdue banner above the
  // register. Fetched independently of the tab/filters being browsed (the
  // endpoint answers for the whole company) and, like that banner, silently
  // renders nothing on a failure rather than a red banner over a side detail.
  const [expectationsByPeriod, setExpectationsByPeriod] = useState<Record<string, InvoiceExpectationPeriod>>({});
  const expectationsRequestRef = useRef(0);
  // Bumped by the background poll (see below) so the inline expectations
  // catch up with mail that arrived since — without reusing `reloadToken`,
  // which also reloads every open month the loud way.
  const [expectationsPollTick, setExpectationsPollTick] = useState(0);
  useEffect(() => {
    if (!companyId) return;
    const requestId = ++expectationsRequestRef.current;
    fetchInvoiceExpectations(companyId)
      .then((data) => {
        if (requestId !== expectationsRequestRef.current) return;
        const map: Record<string, InvoiceExpectationPeriod> = {};
        (data.periods || []).forEach((p) => {
          map[p.period] = p;
        });
        setExpectationsByPeriod(map);
      })
      .catch((err) => {
        console.error("Failed to load monthly invoice expectations", err);
      });
  }, [companyId, reloadToken, expectationsPollTick]);

  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [printingKey, setPrintingKey] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Per-month request ids — a stale response (e.g. two "next page" clicks
  // resolving out of order) must not overwrite a newer one. Keyed rather than
  // a single counter because months load independently of each other.
  const monthRequestRef = useRef<Record<string, number>>({});
  const periodsRequestRef = useRef(0);
  // The request id of a LOUD periods load still in flight (0 = none). The
  // background poll shares periodsRequestRef — it must, or a tick fetched for
  // the previous tab could land after a tab switch — so it simply stays out
  // of the way of a loud load instead of superseding it and then possibly
  // dropping its own answer.
  const loudPeriodsRequestRef = useRef(0);
  // Bumped whenever what a month's documents are filtered by changes
  // (tab/search/flags). A month response is only ever rendered if it was
  // started under the generation that is still current — otherwise a request
  // begun under the previous tab could land after the reset and be shown as
  // this month's documents under a filter that never asked for them. Mirrors
  // the flat list's own `requestRef` guard, just carried per-generation
  // instead of per-request since a stale key can hold no in-flight request at
  // all (see loadMonth/toggleExpand).
  const monthGenerationRef = useRef(0);
  // What the currently APPLIED `periods` were fetched for. While this differs
  // from the tab/filters the tree is asked to show right now, the headers on
  // screen are the previous query's answer — stale numbers a click on the
  // wrong month or button would otherwise act on as if they applied to the
  // new query (see the `isStale` render below).
  const [appliedFor, setAppliedFor] = useState<string>("");
  // The tab the applied headers were counted for. Labels read THIS, not the
  // live `tab`: after a tab click the previous response stays on screen until
  // the new one lands, and relabelling its numbers with the new tab's name
  // would print the new tab's label over the old tab's count (bugbot #712).
  const [appliedTab, setAppliedTab] = useState<PurchaseDocTab>(tab);
  const loadPeriods = useCallback(async (options?: { quiet?: boolean }) => {
    if (!companyId) return;
    const quiet = Boolean(options && options.quiet);
    const writeSeq = writeSeqRef.current;
    const requestId = ++periodsRequestRef.current;
    if (!quiet) loudPeriodsRequestRef.current = requestId;
    // The spinner is not a flag anyone raises and lowers — it is simply "we
    // have nothing to show yet", and only a response that is actually APPLIED
    // ends that. Two earlier shapes both failed here: tying the teardown to
    // "was I the first call" left the spinner up forever when a refresh
    // overlapped the first fetch, and lowering it in `finally` let a
    // superseded call hand the screen to the empty-handover copy while the
    // newer request was still in flight (bugbot #696, twice). A tab/filter
    // change reuses the same spinner-avoidance: `loaded` is never lowered for
    // it (see the reset effect below) — the previous headers stay on screen
    // until this response is actually applied.
    try {
      const data = await fetchPurchasePeriods(companyId, { tab, filtersValue: filters });
      if (requestId !== periodsRequestRef.current) return;
      // A background refresh that raced one of our own writes carries the
      // counts from before it — the next tick will bring the settled ones.
      if (quiet && writeSeq !== writeSeqRef.current) return;
      const nextPeriods = data.periods || [];
      // A month that turned grey (or vanished) between two responses must not
      // keep its previously-loaded rows counted in the bulk selection — the
      // selection is of rows the person can currently SEE, and a grey month
      // shows none. Pruning both `expandedKeys` and `monthState` here, rather
      // than in the tab/filter-change reset effect, is what catches this: the
      // tab/filters did not change, only what this month now holds under them
      // (a doc left it after a write elsewhere — see the reloadToken effect).
      const liveKeys = new Set(nextPeriods.filter((p) => p.count > 0).map((p) => periodKey(p.period)));
      setPeriods(nextPeriods);
      setExpandedKeys((prev) => {
        const next = new Set(Array.from(prev).filter((key) => liveKeys.has(key)));
        return next.size === prev.size ? prev : next;
      });
      setMonthState((prev) => {
        let changed = false;
        const next: Record<string, MonthState> = {};
        Object.keys(prev).forEach((key) => {
          if (liveKeys.has(key)) {
            next[key] = prev[key];
          } else {
            changed = true;
          }
        });
        return changed ? next : prev;
      });
      setLoadError(false);
      setLoaded(true);
      setAppliedFor(JSON.stringify({ tab, filters }));
      setAppliedTab(tab);
    } catch (err) {
      if (requestId !== periodsRequestRef.current) return;
      // A failed background refresh is not worth a red banner over headers
      // that are still perfectly usable — the next tick simply tries again.
      if (quiet) return;
      console.error("Failed to load the invoice period tree", err);
      setLoadError(true);
      setLoaded(true);
    } finally {
      if (loudPeriodsRequestRef.current === requestId) loudPeriodsRequestRef.current = 0;
    }
  }, [companyId, tab, filters]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const loadMonth = useCallback(
    async (period: string | null, page: number, quiet = false) => {
      const key = periodKey(period);
      const generation = monthGenerationRef.current;
      const writeSeq = writeSeqRef.current;
      const requestId = (monthRequestRef.current[key] || 0) + 1;
      monthRequestRef.current[key] = requestId;
      // Quiet = the background poll: the rows stay on screen while it runs
      // (swapping the table for a spinner every minute would make the month
      // jump under the reader), and a failure keeps them rather than
      // replacing them with an error.
      if (!quiet) {
        setMonthState((prev) => ({ ...prev, [key]: { ...(prev[key] || EMPTY_MONTH_STATE), loading: true, error: false } }));
      }
      try {
        const data = await fetchPurchaseDocs(companyId, {
          page,
          tab,
          filtersValue: { ...filters, period: key },
        });
        if (generation !== monthGenerationRef.current || monthRequestRef.current[key] !== requestId) return;
        if (quiet && writeSeq !== writeSeqRef.current) return;
        setMonthState((prev) => ({
          ...prev,
          [key]: { loading: false, error: false, docs: data.docs || [], pagination: data.pagination || null, page },
        }));
      } catch (err) {
        if (generation !== monthGenerationRef.current || monthRequestRef.current[key] !== requestId) return;
        if (quiet) return;
        console.error("Failed to load the month's documents", err);
        setMonthState((prev) => ({ ...prev, [key]: { ...(prev[key] || EMPTY_MONTH_STATE), loading: false, error: true } }));
      }
    },
    [companyId, tab, filters]
  );

  // Changing what you're browsing by (tab/search/flags) invalidates whatever
  // was open — reload from a clean slate rather than show stale rows under a
  // filter that no longer applies to them. Bumping the generation here, not
  // just clearing the state, is what stops a response already in flight for
  // the previous tab from landing afterwards and being rendered as if it
  // belonged to the new one (loadMonth checks it on return).
  // Two resets, because they are about different things.
  //
  // A tab, search or flag change invalidates the DOCUMENTS inside a month —
  // and now, unlike before, the HEADERS themselves too (they are scoped to
  // tab+filters). `loaded` is deliberately never lowered here: the previous
  // headers stay on screen until `loadPeriods`'s new response is applied,
  // exactly as the comment above it explains (bugbot #696).
  useEffect(() => {
    monthGenerationRef.current += 1;
    setExpandedKeys(new Set());
    setMonthState({});
  }, [tab, filters]);

  // A company change invalidates everything, headers included. `loaded` goes
  // back to false so the spinner shows instead of the previous company's
  // periods — and an open month must not keep showing the previous company's
  // documents under the new company's header, which is the one thing a
  // register serving several companies must never do.
  useEffect(() => {
    monthGenerationRef.current += 1;
    setExpandedKeys(new Set());
    setMonthState({});
    setLoaded(false);
  }, [companyId]);

  // Refreshes the open months in place, for the panel's changes and the poll.
  // A month that is mid-load (just expanded, or a page change) is left alone: a
  // quiet request superseding that load and then failing or being dropped
  // would leave its spinner up, and one that succeeds would paint the page the
  // reader was on over the page they just asked for.
  const reloadOpenMonthsQuietly = () => {
    expandedKeys.forEach((key) => {
      if (monthState[key]?.loading) return;
      loadMonth(keyToPeriod(key), monthState[key]?.page || 1, true);
    });
  };

  // The doc panel can approve/reject/delete/restore a document, which changes
  // which period it counts under or whether it counts at all. Refresh the
  // summary and whatever is currently open, without collapsing it — and the
  // open months quietly, so their tables do not blink into a spinner and back
  // every time the panel saves something.
  const isFirstReload = useRef(true);
  useEffect(() => {
    if (isFirstReload.current) {
      isFirstReload.current = false;
      return;
    }
    loadPeriods();
    reloadOpenMonthsQuietly();
    // Only the token should trigger this — it fires once per real change and
    // reads the latest expandedKeys/monthState via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  // A month keeps its previous rows while it reloads, so a selection survives
  // the refresh after a bulk action; collapsing a month drops its rows from it.
  useEffect(() => {
    const shown: PurchaseDocListItem[] = [];
    expandedKeys.forEach((key) => {
      const state = monthState[key];
      if (state) shown.push(...state.docs);
    });
    onShownDocsChange(shown);
  }, [expandedKeys, monthState, onShownDocsChange]);

  // The register's only live refresh now that the flat list (which used to
  // poll) is gone: new mail, and documents finishing being read, have to show
  // up without anyone touching the page. Quiet all the way down — headers
  // swap in place, open months keep their rows while refetching, failures
  // keep what is on screen — and never while the tab is hidden (nobody is
  // looking, and a phone would pay for it) or while a write is running (a
  // refresh landing mid-toggle would paint the old value back; the write
  // sequence guard in loadPeriods/loadMonth covers the ones already in flight).
  // Only EXPANDED months' rows count: a collapsed month keeps its last rows in
  // `monthState`, but the poll never refetches it, so a "pending" left there
  // would never clear and would pin the fast interval for good.
  const hasReading =
    periods.some((p) => p.byStatus && p.byStatus.new > 0) ||
    Array.from(expandedKeys).some((key) =>
      (monthState[key]?.docs || []).some((doc) => doc.extractionStatus === "pending")
    );
  const pollBlocked = pollPaused || Boolean(busyDocId) || Boolean(paidBusyDocId);
  const pollRef = useRef<() => void>(() => undefined);
  pollRef.current = () => {
    if (document.hidden || pollBlocked || !loaded) return;
    // A loud load already in flight is fetching the same thing fresher; let
    // it finish rather than supersede it (see loudPeriodsRequestRef).
    if (!loudPeriodsRequestRef.current) loadPeriods({ quiet: true });
    reloadOpenMonthsQuietly();
    setExpectationsPollTick((n) => n + 1);
    if (onCountsStale) onCountsStale();
  };
  useEffect(() => {
    if (!companyId) return undefined;
    const tick = () => pollRef.current();
    const intervalId = window.setInterval(tick, hasReading ? READING_POLL_MS : IDLE_POLL_MS);
    // Coming back to the tab is when staleness shows most — do not make the
    // reader wait out the rest of the interval.
    const onVisible = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [companyId, hasReading]);

  const toggleExpand = (period: string | null) => {
    const key = periodKey(period);
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // Always refetch on expand. A cached entry is not trusted: it can be a
        // page that landed after the month was pruned (loadPeriods) or after
        // it was collapsed mid-request, and would then sit there stale or
        // stuck on its spinner. The previous rows stay in state meanwhile.
        loadMonth(period, 1);
      }
      return next;
    });
  };

  // A pill or a shared link can name a month directly (?period=). Expand it
  // once it appears in `periods` with rows under the current tab — never for
  // a grey (count 0) month, which cannot expand at all. Ref-guarded per
  // (tab, key) so this fires exactly once per landing, not on every reload of
  // the periods it depends on.
  // The tree survives a tab switch (it is rendered outside the Polaris Tabs
  // panel), so a pill click arrives here with the PREVIOUS tab's periods still
  // in state and the reset effect above clearing the months in the same
  // commit. Two rules keep that from opening an empty month: only periods
  // APPLIED for the current tab+filters count (`appliedFor`), and the month
  // is always fetched — never "already loaded", the reset just dropped it.
  // The guard is armed per landing and re-armed whenever the tab or the named
  // period changes, so bouncing between tabs through pills expands each time
  // while a reload of the same periods does not re-expand a collapsed month.
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    autoExpandedRef.current = false;
  }, [tab, expandPeriodKey]);
  useEffect(() => {
    if (!expandPeriodKey || autoExpandedRef.current) return;
    if (appliedFor !== JSON.stringify({ tab, filters })) return;
    const target = periods.find((p) => periodKey(p.period) === expandPeriodKey);
    if (!target || target.count <= 0) return;
    autoExpandedRef.current = true;
    setExpandedKeys((prev) => (prev.has(expandPeriodKey) ? prev : new Set(prev).add(expandPeriodKey)));
    loadMonth(keyToPeriod(expandPeriodKey), 1);
    // loadMonth deliberately excluded — the guard above is what makes this
    // run once per landing, not the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods, appliedFor, expandPeriodKey, tab, filters]);

  const adjustBookkeptCount = (key: string, delta: number, alsoEnteredCount: boolean) => {
    setPeriods((prev) =>
      prev.map((p) => {
        if (periodKey(p.period) !== key) return p;
        const next: PurchasePeriod = { ...p, bookkeptCount: p.bookkeptCount + delta };
        // Only a bookkeepable status moves the "Įvesta X iš Y" badge — ticking
        // the box on e.g. a rejected doc (which the badge never counted) must
        // not nudge it, mirroring erp-backend's own BOOKKEEPABLE_STATUSES gate.
        if (alsoEnteredCount) {
          next.enteredCount = p.enteredCount + delta;
          next.notEnteredCount = p.notEnteredCount - delta;
          // The same move splits the month's Patvirtintos/Įvestos pills.
          next.approvedBookkept = Math.max(0, (p.approvedBookkept || 0) + delta);
        }
        return next;
      })
    );
  };

  const patchMonthDoc = (key: string, docId: string, changes: Partial<PurchaseDocListItem>) => {
    setMonthState((prev) => {
      const state = prev[key];
      if (!state) return prev;
      return {
        ...prev,
        [key]: { ...state, docs: state.docs.map((d) => (d.id === docId ? { ...d, ...changes } : d)) },
      };
    });
  };

  const setDocBookkeptAt = (key: string, docId: string, bookkeptAt: number | null) =>
    patchMonthDoc(key, docId, { bookkeptAt });

  const toggleBookkept = async (period: string | null, doc: PurchaseDocListItem) => {
    const key = periodKey(period);
    const nextEntered = !doc.bookkeptAt;
    const previousAt = doc.bookkeptAt;
    const alsoEnteredCount = isBookkeepable(doc.status);
    writeSeqRef.current += 1;
    setBusyDocId(doc.id);
    setBookkeptError(null);
    setDocBookkeptAt(key, doc.id, nextEntered ? Date.now() : null);
    adjustBookkeptCount(key, nextEntered ? 1 : -1, alsoEnteredCount);
    try {
      const { doc: updated } = await setPurchaseDocBookkept(companyId, doc.id, nextEntered);
      setDocBookkeptAt(key, doc.id, updated.bookkeptAt ?? null);
      // On these two tabs the row now belongs to the OTHER one (see
      // erp-backend's approved/bookkept split) — a local patch would leave it
      // sitting here under a tab it no longer matches until the next
      // unrelated reload. Every other tab's toggle stays purely optimistic.
      if (tab === "approved" || tab === "bookkept") {
        onDocMoved();
      } else if (alsoEnteredCount && onCountsStale) {
        // Any other tab keeps the row, but a bookkeepable document still
        // moved between the Patvirtintos and Įvestos tab badges.
        onCountsStale();
      }
    } catch (err) {
      // The server never accepted the change — put the checkbox (and the
      // month's counts) back exactly where they were.
      setDocBookkeptAt(key, doc.id, previousAt);
      adjustBookkeptCount(key, nextEntered ? -1 : 1, alsoEnteredCount);
      setBookkeptError(t("invoice_inbox_action_failed", { defaultValue: "The action did not go through." }));
    } finally {
      writeSeqRef.current += 1;
      setBusyDocId(null);
    }
  };

  /** The "Apmokėta" tick — independent of the bookkept split above, so it
   * never moves a document between tabs and never needs `onDocMoved`. */
  const toggleMarkedPaid = async (period: string | null, doc: PurchaseDocListItem) => {
    const key = periodKey(period);
    const nextPaid = !isMarkedPaid(doc);
    const previous = { markedPaidAt: doc.markedPaidAt, markedPaidBy: doc.markedPaidBy, markedPaidByName: doc.markedPaidByName };
    writeSeqRef.current += 1;
    setPaidBusyDocId(doc.id);
    setPaidError(null);
    // The name belongs to whoever set the PREVIOUS mark — keeping it after a
    // re-tick would credit the wrong person in the tick's title. It comes back
    // with the next refresh of the month; until then the title is nameless.
    patchMonthDoc(key, doc.id, { markedPaidAt: nextPaid ? Date.now() : null, markedPaidByName: null });
    try {
      const { doc: updated } = await setPurchaseDocMarkedPaid(companyId, doc.id, nextPaid);
      patchMonthDoc(key, doc.id, {
        markedPaidAt: updated.markedPaidAt ?? null,
        markedPaidBy: updated.markedPaidBy ?? null,
      });
    } catch (err) {
      patchMonthDoc(key, doc.id, previous);
      setPaidError(t("invoice_inbox_action_failed", { defaultValue: "The action did not go through." }));
    } finally {
      writeSeqRef.current += 1;
      setPaidBusyDocId(null);
    }
  };

  const handleDownload = async (period: string | null) => {
    const key = periodKey(period);
    setDownloadingKey(key);
    setDownloadError(null);
    try {
      await downloadPurchasePeriodExport(companyId, period, { tab, filtersValue: filters });
    } catch (err) {
      setDownloadError(exportErrorMessage(err, t));
    } finally {
      setDownloadingKey(null);
    }
  };

  // The PDF list prints what the month shows: same tab, same filters.
  const handlePrint = async (period: string | null) => {
    const key = periodKey(period);
    setPrintingKey(key);
    setDownloadError(null);
    try {
      await downloadPurchasePeriodRegisterPdf(companyId, period, {
        tab,
        filtersValue: filters,
        lang: (i18n as any).language === "en" ? "en" : "lt",
      });
    } catch (err) {
      setDownloadError(exportErrorMessage(err, t));
    } finally {
      setPrintingKey(null);
    }
  };

  const buildMonthColumns = (period: string | null, docs: PurchaseDocListItem[]): Column[] => [
    buildSelectColumn({
      t,
      rows: docs,
      selectedIds,
      disabled: selectionDisabled,
      onToggle: onToggleSelected,
      onSetMany: onSetManySelected,
    }),
    buildPreviewColumn(t, onPreview),
    {
      key: "supplierNameRaw",
      name: t("invoice_inbox_col_supplier", { defaultValue: "Supplier" }),
      type: "custom",
      mobileView: "topLeft",
      render: (item: PurchaseDocListItem) => (
        <div>
          <div>{item.supplierNameRaw || t("invoice_inbox_unknown_supplier", { defaultValue: "Not recognised" })}</div>
          {!item.supplier && (
            <div className="text-xs text-amber-700">
              {t("invoice_inbox_supplier_unresolved", { defaultValue: "not linked" })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "invoiceNumber",
      name: t("invoice_inbox_col_invoice_number", { defaultValue: "Invoice no." }),
      type: "custom",
      render: (item: PurchaseDocListItem) => invoiceNumberWithSeries(item) || "—",
    },
    { key: "issueDate", name: t("invoice_inbox_col_date", { defaultValue: "Date" }), type: "date" },
    {
      key: "dueDate",
      name: t("invoice_inbox_col_due", { defaultValue: "Due" }),
      type: "custom",
      mobileView: "bottomRight",
      render: (item: PurchaseDocListItem) => (
        <span className={isOverdue(item) ? "text-red-600 font-semibold" : ""}>
          {/* The phone card has no column headers, so a bare date would not say which date it is.
              isPhone is the same width test the DataTable uses to draw its card, so this cell
              (and the currency beside the total) is only decorated when it is drawn in a card. */}
          {isPhone && `${t("invoice_inbox_col_due", { defaultValue: "Due" })} `}
          {item.dueDate ? formatPrettyDate(item.dueDate) : "—"}
        </span>
      ),
    },
    {
      key: "netAmount",
      name: t("net", { defaultValue: "Net" }),
      type: "custom",
      render: (item: PurchaseDocListItem) => (
        <span className="tabular-nums">{toAmount(item.netAmount)?.toFixed(2) ?? "—"}</span>
      ),
    },
    {
      key: "vatAmount",
      name: t("vat", { defaultValue: "VAT" }),
      type: "custom",
      render: (item: PurchaseDocListItem) => (
        <span className="tabular-nums">{toAmount(item.vatAmount)?.toFixed(2) ?? "—"}</span>
      ),
    },
    {
      // The VMI VAT code of each VAT line (taxed lines first). With several
      // lines each code shows the amount it covers, so a partly taxed invoice
      // (Tele2: PVM1 96.86 + PVM5 3.70) explains why VAT is not 21 % of the net.
      key: "pvmCode",
      name: t("invoice_inbox_pvm_code", { defaultValue: "VAT code" }),
      type: "custom",
      render: (item: PurchaseDocListItem) => {
        const lines = item.vatLines ?? [];
        if (lines.length === 0) return <span>—</span>;
        return (
          <div className="whitespace-nowrap">
            {lines.map((line) => (
              <div
                key={line.id}
                className={isUncertainPvmLine(line) ? "text-amber-700" : undefined}
                title={[line.pvmClassifier && pvmCodeLabel(t, line.pvmClassifier), pvmReasonLabel(t, line)]
                  .filter(Boolean)
                  .join(" · ")}
              >
                {line.pvmClassifier ?? "—"}
                {lines.length > 1 && (
                  <span className="ml-1 text-xs text-gray-500 tabular-nums">
                    {toAmount(line.taxableValue)?.toFixed(2)}
                  </span>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "grossAmount",
      name: t("total", { defaultValue: "Total" }),
      type: "custom",
      mobileView: "topRight",
      render: (item: PurchaseDocListItem) => (
        <div>
          <span className="tabular-nums font-semibold">{toAmount(item.grossAmount)?.toFixed(2) ?? "—"}</span>
          {/* The phone card has no Currency column next to the amount. */}
          {isPhone && <span className="ml-1 text-xs text-gray-500">{item.currency}</span>}
          {isForeignCurrency(item) && (
            <div className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
              {formatEurEquivalent(item) ?? t("invoice_inbox_fx_pending_short", { defaultValue: "EUR: no rate yet" })}
            </div>
          )}
        </div>
      ),
    },
    { key: "currency", name: t("currency", { defaultValue: "Currency" }), type: "string" },
    {
      key: "payment",
      name: t("invoice_inbox_col_payment", { defaultValue: "Payment" }),
      type: "custom",
      render: (item: PurchaseDocListItem) => {
        const paid = isMarkedPaid(item);
        // A payment the import has actually seen cannot be unticked by
        // clearing the manual mark — see isMarkedPaid — so that case gets its
        // own title instead of the (disabled) "click to undo" wording.
        const viaImportOnly = !item.markedPaidAt && Boolean(item.paidAt);
        const paidTitle = !paid
          ? t("invoice_inbox_paid_toggle_off", { defaultValue: "Mark as paid" })
          : viaImportOnly
            ? t("invoice_inbox_paid_toggle_import", { defaultValue: "Paid — from the payment import" })
            : isMarkedPaidAutomatically(item)
              ? t("invoice_inbox_marked_paid_auto", {
                  defaultValue: "Marked automatically: this supplier's invoices arrive paid, {{date}}",
                  date: formatPrettyDate(item.markedPaidAt),
                })
              : item.markedPaidByName
                ? t("invoice_inbox_paid_toggle_on_by", {
                    defaultValue: "Paid — marked by {{name}}, {{date}}. Click to undo",
                    name: item.markedPaidByName,
                    date: formatPrettyDate(item.markedPaidAt),
                  })
                : // Right after an optimistic toggle the name is not known yet
                  // (only `markedPaidAt` is patched locally) — fall back to the
                  // nameless title rather than show a stale or missing one.
                  t("invoice_inbox_paid_toggle_on", { defaultValue: "Paid — click to undo" });
        const payerLabel =
          item.paymentSource === "employee"
            ? item.paidByEmployeeName ||
              t("invoice_inbox_payment_source_employee", { defaultValue: "Paid by an employee" })
            : item.paymentSource === "company"
            ? t("invoice_inbox_payment_source_company_short", { defaultValue: "Company" })
            : null;
        const textClass = payerLabel === null ? "text-gray-400" : paid ? "text-green-700" : "text-red-600";
        return (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className={clsx(
                "inline-flex shrink-0 items-center justify-center rounded p-0.5 hover:bg-gray-100 disabled:opacity-40",
                touchHitAreaClass
              )}
              disabled={paidBusyDocId === item.id || Boolean(item.paidAt)}
              aria-pressed={paid}
              title={paidTitle}
              aria-label={paidTitle}
              onClick={(event) => {
                event.stopPropagation();
                toggleMarkedPaid(period, item);
              }}
            >
              {paid ? (
                <CircleCheck className="h-4 w-4 text-green-600" />
              ) : (
                <CircleDashed className="h-4 w-4 text-red-500" />
              )}
            </button>
            <span className={`text-sm ${textClass}`}>{payerLabel ?? "—"}</span>
          </div>
        );
      },
    },
    {
      key: "status",
      name: t("invoice_inbox_col_status", { defaultValue: "Status" }),
      type: "custom",
      mobileView: "bottomLeft",
      // "Waiting for you" came from the flat list, which is gone — the month
      // table is now the only place a person sees that a slot is theirs.
      render: (item: PurchaseDocListItem) => (
        <div className="flex flex-wrap items-center gap-1">
          <Badge tone={getStatusTone(item.status)}>{t(getStatusLabelKey(item.status), { defaultValue: item.status })}</Badge>
          {item.waitingForMe && (
            <Badge tone="warning">{t("invoice_inbox_waiting_for_me", { defaultValue: "Waiting for you" })}</Badge>
          )}
        </div>
      ),
    },
    {
      key: "bookkeptAt",
      name: t("invoice_inbox_col_bookkept", { defaultValue: "Entered" }),
      type: "custom",
      render: (item: PurchaseDocListItem) => {
        const entered = !!item.bookkeptAt;
        return (
          <button
            type="button"
            className={clsx(
              "inline-flex items-center justify-center rounded p-0.5 hover:bg-gray-100 disabled:opacity-40",
              touchHitAreaClass
            )}
            disabled={busyDocId === item.id}
            aria-pressed={entered}
            title={
              entered
                ? t("invoice_inbox_bookkept_toggle_on", { defaultValue: "Entered — click to undo" })
                : t("invoice_inbox_bookkept_toggle_off", { defaultValue: "Mark as entered" })
            }
            aria-label={
              entered
                ? t("invoice_inbox_bookkept_toggle_on", { defaultValue: "Entered — click to undo" })
                : t("invoice_inbox_bookkept_toggle_off", { defaultValue: "Mark as entered" })
            }
            onClick={(event) => {
              event.stopPropagation();
              toggleBookkept(period, item);
            }}
          >
            {entered ? (
              <CircleCheck className="h-4 w-4 text-green-600" />
            ) : (
              <Circle className="h-4 w-4 text-gray-300 hover:text-gray-500" />
            )}
          </button>
        );
      },
    },
  ];

  /**
   * The month's own totals, as rows under the columns they belong to instead
   * of loose text above the table. These come from the server's per-month
   * aggregate (`p.totals`/`p.totalEur`/`p.missingAmountsCount`) — the table
   * itself is paginated, so a client-side sum would only ever cover one page.
   */
  const buildMonthFooterRows = (p: PurchasePeriod): FooterRow[] => {
    // Under the columns of the table an amount needs no label; the phone's summary box
    // lists the bare values side by side, so there each one says what it is.
    const footerAmount = (label: string, value: number, className = "tabular-nums") => (
      <span className={className}>
        {isPhone && <span className="mr-1 font-normal text-gray-500">{label}</span>}
        {value.toFixed(2)}
      </span>
    );
    const netLabel = t("net", { defaultValue: "Net" });
    const vatLabel = t("vat", { defaultValue: "VAT" });
    const grossLabel = t("total", { defaultValue: "Total" });

    const rows: FooterRow[] = p.totals.map((total) => ({
      key: `total-${total.currency}`,
      label: t("invoice_inbox_period_month_total_currency", {
        currency: total.currency,
        defaultValue: "Month total, {{currency}}",
      }),
      cells: {
        netAmount: footerAmount(netLabel, total.net),
        vatAmount: footerAmount(vatLabel, total.vat),
        grossAmount: footerAmount(grossLabel, total.gross, "tabular-nums font-semibold"),
        currency: total.currency,
      },
    }));

    // A month with any foreign-currency document also gets its one
    // cross-currency figure: everything in euro.
    if (p.totalEur && p.totals.some((total) => isForeignCurrency(total))) {
      rows.push({
        key: "total-eur",
        className: "font-semibold text-gray-700",
        label: (
          <span>
            {t("invoice_inbox_period_total_eur", { defaultValue: "All in EUR" })}
            {p.totalEur.unconverted > 0 && (
              <span className="ml-1 font-normal text-amber-700">
                (
                {t("invoice_inbox_period_unconverted", {
                  count: p.totalEur.unconverted,
                  defaultValue: "without a rate yet, not included: {{count}}",
                })}
                )
              </span>
            )}
          </span>
        ),
        cells: {
          netAmount: footerAmount(netLabel, p.totalEur.net),
          vatAmount: footerAmount(vatLabel, p.totalEur.vat),
          grossAmount: footerAmount(grossLabel, p.totalEur.gross),
          currency: "EUR",
        },
      });
    }

    if (p.missingAmountsCount > 0) {
      rows.push({
        key: "missing-amounts",
        className: "text-amber-700",
        note: t("invoice_inbox_period_missing_amounts", {
          count: p.missingAmountsCount,
          defaultValue: "No net / VAT amounts: {{count}} doc.",
        }),
      });
    }

    return rows;
  };

  if (!loaded) {
    return (
      <div className="flex justify-center p-8">
        <Spinner accessibilityLabel="Loading" size="large" />
      </div>
    );
  }

  if (loadError) {
    return (
      <Banner tone="critical" action={{ content: t("retry", { defaultValue: "Retry" }), onAction: loadPeriods }}>
        {t("invoice_inbox_period_load_failed", {
          defaultValue: "Could not load the periods. Nothing has been lost — try again.",
        })}
      </Banner>
    );
  }

  const yearGroups: Array<{ year: string; periods: PurchasePeriod[] }> = [];
  let nullPeriodEntry: PurchasePeriod | null = null;
  for (const p of periods) {
    if (p.period === null) {
      nullPeriodEntry = p;
      continue;
    }
    const year = p.period.slice(0, 4);
    const current = yearGroups[yearGroups.length - 1];
    if (!current || current.year !== year) {
      yearGroups.push({ year, periods: [p] });
    } else {
      current.periods.push(p);
    }
  }

  if (!yearGroups.length && !nullPeriodEntry) {
    return <>{emptyState}</>;
  }

  // True while `periods` is still the ANSWER TO A DIFFERENT QUERY than the
  // tab/filters the tree is being asked to show right now — the previous
  // response stays on screen (see the comment on `loadPeriods`) but a click
  // on it would expand a month, print or download under the OLD tab/filters,
  // not the new ones the person just chose. Disabled rather than hidden: the
  // stale numbers are still useful to glance at while the fresh ones load.
  const isStale = appliedFor !== JSON.stringify({ tab, filters });

  // "Patvirtintos: 8" for a status tab, "Dokumentų: N" (unchanged) for 'all' —
  // both the header's own count and the year line reuse this.
  const tabCountLabel = (count: number) =>
    appliedTab === "all"
      ? t("invoice_inbox_period_docs_count", { count, defaultValue: "{{count}} document(s)" })
      : t("invoice_inbox_period_tab_count", {
          tab: t(TAB_LABEL_KEYS[appliedTab]),
          count,
          defaultValue: "{{tab}}: {{count}}",
        });

  const renderPill = (bucket: PillBucket, key: string) => {
    // A pill is named after where it takes you — the TAB — so "Tvirtinamos 3"
    // reads the same on the pill and on the tab it opens. Only the bucket
    // without a tab (documents still being read) keeps its status label.
    // Only the tab-less bucket ("new" — still being read) ever reaches the
    // status-label branch; "bookkept" always carries a tab. The cast reflects
    // that, since PillBucket's key is widened to admit "bookkept" too.
    const text = bucket.tab
      ? t(TAB_LABEL_KEYS[bucket.tab])
      : t(getStatusLabelKey(bucket.key as PurchaseDocStatus), { defaultValue: bucket.key });
    const label = `${text} ${bucket.count}`;
    const className = `${PILL_CLASS} ${PILL_TONE_CLASS[bucket.key]}`;
    // The bucket for the tab already being browsed is plain text — a link to
    // where you already are is not a link.
    if (bucket.tab && bucket.tab !== tab) {
      return (
        <Link key={bucket.key} to={chipUrl(bucket.tab, key)} className={className} aria-label={`${label} — ${t(TAB_LABEL_KEYS[bucket.tab])}`}>
          {label}
        </Link>
      );
    }
    return (
      <span key={bucket.key} className={className} aria-label={label}>
        {label}
      </span>
    );
  };

  const renderMonthRow = (p: PurchasePeriod) => {
    const key = periodKey(p.period);
    const isOpen = expandedKeys.has(key);
    const state = monthState[key];
    const label = p.period ? monthLabel(p.period, (i18n as any).language) : t("invoice_inbox_period_unassigned", {
      defaultValue: "Period not determined",
    });
    const isGrey = p.count === 0;
    const pillBuckets = buildPillBuckets(p);

    const secondLine = tab !== "deleted" && (
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span>
          {t("invoice_inbox_period_month_total", {
            count: p.monthTotal,
            defaultValue: "This month in total {{count}}:",
          })}
        </span>
        {pillBuckets.map((bucket) => renderPill(bucket, key))}
      </div>
    );

    // Monthly-invoice expectations for THIS month, inline rather than only as
    // the overdue banner above the register — works on a grey month exactly
    // like a live one, since a supplier due to invoice a quiet month is
    // exactly the point. A month absent from the map (an older server, or one
    // with nothing due) simply shows nothing here.
    const expectation = p.period ? expectationsByPeriod[p.period] : undefined;
    const supplierLabel = (row: { supplierName: string | null }) =>
      row.supplierName || t("invoice_inbox_expectations_unnamed_supplier", { defaultValue: "Unnamed supplier" });
    const expectationsLine = tab !== "deleted" && expectation && expectation.expected > 0 && (
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-500">
          {t("invoice_inbox_expectations_month_summary", {
            expected: expectation.expected,
            received: expectation.received,
            defaultValue: "Monthly invoices expected: {{expected}} · received {{received}}",
          })}
        </span>
        {expectation.pending.map((row) => (
          <span
            key={`pending-${row.supplier}`}
            className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600"
          >
            {t("invoice_inbox_expectations_chip_pending", {
              supplier: supplierLabel(row),
              date: formatPrettyDate(row.expectedBy),
              defaultValue: "{{supplier}} (expected by {{date}})",
            })}
          </span>
        ))}
        {expectation.missing.map((row) => (
          <span
            key={`missing-${row.supplier}`}
            className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-red-700"
          >
            {t("invoice_inbox_expectations_chip_missing", {
              supplier: supplierLabel(row),
              date: formatPrettyDate(row.expectedBy),
              defaultValue: "{{supplier}} — overdue (due by {{date}})",
            })}
          </span>
        ))}
        {expectation.waived > 0 && (
          <span className="text-gray-400">
            {t("invoice_inbox_expectations_waived_count", {
              count: expectation.waived,
              defaultValue: "not expected: {{count}}",
            })}
          </span>
        )}
      </div>
    );

    if (isGrey) {
      return (
        <div key={key} className="bg-white rounded-sm border border-[#E6E1DB] overflow-hidden">
          <Box padding="400">
            <div className={isStale ? "opacity-60" : undefined}>
            <button
              type="button"
              disabled
              aria-disabled="true"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "default",
                textAlign: "left",
              }}
            >
              <Text as="span" variant="headingSm" fontWeight="semibold" tone="subdued">
                {label}
              </Text>
              <span className="text-gray-400">
                <Badge>{tabCountLabel(0)}</Badge>
              </span>
            </button>
            {secondLine}
            {expectationsLine}
            </div>
          </Box>
        </div>
      );
    }

    return (
      <div key={key} className="bg-white rounded-sm border border-[#E6E1DB] overflow-hidden">
        <Box padding="400">
          <div className={isStale ? "opacity-60" : undefined}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <button
              type="button"
              onClick={() => toggleExpand(p.period)}
              disabled={isStale}
              aria-expanded={isOpen}
              aria-disabled={isStale || undefined}
              // 44px high on a phone and on a touch screen: the line of text alone is 20px.
              className="max-md:min-h-11 [@media(pointer:coarse)]:min-h-11"
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <Icon source={(isOpen ? ChevronUpIcon : ChevronDownIcon) as any} />
              <Text as="span" variant="headingSm" fontWeight="semibold">
                {label}
              </Text>
              <Badge>{tabCountLabel(p.count)}</Badge>
              {/* Always 0/Y or Y/Y on these two tabs — the split IS the
                  badge (see the approved/bookkept pills instead). */}
              {p.enterableCount > 0 && tab !== "approved" && tab !== "bookkept" && (
                <Badge tone={p.enteredCount === p.enterableCount ? "success" : "attention"}>
                  {t("invoice_inbox_period_entered_of", {
                    entered: p.enteredCount,
                    total: p.enterableCount,
                    defaultValue: "Entered {{entered}} of {{total}}",
                  })}
                </Badge>
              )}
            </button>
            <div className="flex items-center gap-4 flex-wrap">
              <Button disabled={isStale} loading={printingKey === key} onClick={() => handlePrint(p.period)}>
                {t("invoice_inbox_period_register_pdf", { defaultValue: "PDF list" })} ({p.count})
              </Button>
              <Button disabled={isStale} loading={downloadingKey === key} onClick={() => handleDownload(p.period)}>
                {t("invoice_inbox_period_download", { defaultValue: "Download the month" })} ({p.count})
              </Button>
            </div>
          </div>
          {secondLine}
          {expectationsLine}
          </div>
        </Box>
        <Collapsible open={isOpen} id={`period-${key}`}>
          <Box padding="400" paddingBlockStart="0">
            {state?.loading && (
              <div className="flex justify-center p-6">
                <Spinner accessibilityLabel="Loading" size="small" />
              </div>
            )}
            {state?.error && (
              <Banner
                tone="critical"
                action={{ content: t("retry", { defaultValue: "Retry" }), onAction: () => loadMonth(p.period, state.page) }}
              >
                {t("invoice_inbox_period_docs_load_failed", {
                  defaultValue: "Could not load this month's documents.",
                })}
              </Banner>
            )}
            {state && !state.loading && !state.error && (
              <>
                <DataTable
                  collection={state.docs}
                  columns={buildMonthColumns(p.period, state.docs)}
                  isTotalsDisabled
                  footerRows={buildMonthFooterRows(p)}
                  rowUrl={rowUrl}
                  rowClassName={(item: PurchaseDocListItem) =>
                    failedIds.has(item.id) ? "bg-amber-50 hover:bg-amber-100" : undefined
                  }
                />
                {state.pagination && state.pagination.total > 0 && (
                  <div className="mt-3">
                    <PaginationControls
                      page={state.pagination.page}
                      pageSize={state.pagination.pageSize}
                      total={state.pagination.total}
                      totalPages={state.pagination.totalPages}
                      hasNextPage={state.pagination.hasNextPage}
                      hasPreviousPage={state.pagination.page > 1}
                      onPageChange={(nextPage) => loadMonth(p.period, nextPage)}
                      onPageSizeChange={() => undefined}
                      pageSizeOptions={[state.pagination.pageSize]}
                    />
                  </div>
                )}
              </>
            )}
          </Box>
        </Collapsible>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {downloadError && (
        <Banner tone="critical" onDismiss={() => setDownloadError(null)}>
          {downloadError}
        </Banner>
      )}
      {bookkeptError && (
        <Banner tone="critical" onDismiss={() => setBookkeptError(null)}>
          {bookkeptError}
        </Banner>
      )}
      {paidError && (
        <Banner tone="critical" onDismiss={() => setPaidError(null)}>
          {paidError}
        </Banner>
      )}
      {yearGroups.map((group) => {
        const yearCount = group.periods.reduce((sum, p) => sum + p.count, 0);
        const yearTotals = new Map<string, number>();
        group.periods.forEach((p) => {
          p.totals.forEach((total) => {
            yearTotals.set(total.currency, (yearTotals.get(total.currency) || 0) + total.gross);
          });
        });
        const sortedYearTotals = Array.from(yearTotals.entries()).sort(([a], [b]) => a.localeCompare(b));
        return (
          <div key={group.year} className="space-y-2">
            {/* `role="group"`/`aria-label` give this a name a test (or a
                screen reader) can look up directly, instead of having to walk
                the DOM from the "2026" heading to find its summary line. */}
            <div className="flex flex-wrap items-baseline gap-x-3" role="group" aria-label={group.year}>
              <Text as="h2" variant="headingMd">
                {group.year}
              </Text>
              <span className="flex items-center gap-2 text-xs text-gray-400">
                <span>{t("invoice_inbox_period_year_docs", { count: yearCount, defaultValue: "{{count}} doc." })}</span>
                {sortedYearTotals.length > 0 && (
                  <span>
                    {t("total", { defaultValue: "Total" })}:{" "}
                    {sortedYearTotals.map(([currency, gross]) => `${gross.toFixed(2)} ${currency}`).join(" · ")}
                  </span>
                )}
              </span>
            </div>
            <div className="space-y-2">{group.periods.map((p) => renderMonthRow(p))}</div>
          </div>
        );
      })}
      {nullPeriodEntry && <div className="space-y-2">{renderMonthRow(nullPeriodEntry)}</div>}
    </div>
  );
}
